import React, { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { unstable_HistoryRouter as HistoryRouter, UNSAFE_createBrowserHistory as createBrowserHistory } from 'react-router-dom';
import { subscribeRecordRefs } from '../utils/recordRefs';
import { createRefHistory, decodeLocation, type Decoded } from './refHistory';
import { subscribeResolution } from './routeResolution';

export type RouteResolution = Pick<Decoded, 'pending' | 'ambiguous' | 'urlProjectHex' | 'projectMismatch'>;

const IDLE: RouteResolution = { pending: [], ambiguous: null, urlProjectHex: null, projectMismatch: false };
const RouteResolutionContext = createContext<RouteResolution>(IDLE);

/** What the current address still needs before its page can open (read by useRouteRefResolution). */
export function useRouteResolution(): RouteResolution {
  return useContext(RouteResolutionContext);
}

/**
 * The app's router. Same behaviour as BrowserRouter, with the address translated at the edge (see refHistory.ts) so
 * `/project/PJ-90F0D6B4/procurement/orders/PO-130` opens the page for `PO-<uuid>` without any page knowing.
 */
export default function RefRouter({ children }: { children: ReactNode }) {
  const history = useMemo(() => createRefHistory(createBrowserHistory({ v5Compat: true })), []);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const bump = () => { history.refresh(); setTick(t => t + 1); };
    const offHistory = history.listen(() => setTick(t => t + 1));
    const offNumbers = subscribeRecordRefs(bump);
    const offAnswers = subscribeResolution(bump);
    // The numbers may have arrived between the first render and this effect.
    bump();
    return () => { offHistory(); offNumbers(); offAnswers(); };
  }, [history]);

  const resolution = useMemo<RouteResolution>(() => {
    const { pending, ambiguous, urlProjectHex, projectMismatch } = decodeLocation(history.location);
    return { pending, ambiguous, urlProjectHex, projectMismatch };
    // tick: the address, the numbers or an answer changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, tick]);

  return (
    <RouteResolutionContext.Provider value={resolution}>
      <HistoryRouter history={history}>{children}</HistoryRouter>
    </RouteResolutionContext.Provider>
  );
}
